// The hazard markers layer, as plain data so it is unit-tested without Mapbox.
//
// Markers come from the Martin vector-tile function source `trail_hazards`,
// through the same-origin `/vector` proxy the web already uses for its other
// Martin overlays — the session cookie authenticates it, and it answers 401 to
// a signed-out visitor, so the source is only added for a signed-in user. See
// the Martin contract shared with iOS and Android (MARTIN-CONTRACT.md).

import { MapLayerCategory } from '../layer-registry';

export const HAZARD_SOURCE_ID = 'trail-hazards-source';
export const HAZARD_LAYER_ID = 'trail-hazards-layer';

/** Martin source id (the proxy path segment) and the MVT layer inside each tile. */
export const HAZARD_MARTIN_SOURCE = 'trail_hazards';
export const HAZARD_SOURCE_LAYER = 'trail_hazards';

/**
 * Markers, and the tiles behind them, start here: only once zoomed in to a few km of trail
 * (product owner, 2026-11-02). The proxy would serve from z9 (MIN_ZOOM_OVERRIDES).
 */
export const HAZARD_MIN_ZOOM = 12;
/** Tiles exist up to here; Mapbox overzooms past it. */
export const HAZARD_MAX_TILE_ZOOM = 16;

/**
 * The web POI and waypoint layers live in the Top band at z 0, so the hazard
 * markers go one step above them there — on top of POIs, as on the apps.
 */
export const HAZARD_LAYER_CATEGORY = MapLayerCategory.Top;
export const HAZARD_LAYER_Z = 1;

/**
 * The marker size. The sprite's hazard triangles are 90×82 at 1x, so 0.4 draws
 * them 36 px wide — the size of the old disc markers and of the apps'. Centred
 * on the point, like the POI layer's `puck_icon_*` markers.
 */
export const HAZARD_ICON_SIZE = 0.4;
export const HAZARD_ICON_ANCHOR = 'center';

/** Feature properties (Martin contract). */
export const HAZARD_PROP_ID = 'id';
export const HAZARD_PROP_ICON = 'icon';
export const HAZARD_PROP_SORT = 'sort';

/**
 * `{origin}/vector/trail_hazards/{z}/{x}/{y}`, plus `?t=<version>` once the
 * source has been refreshed — the cache-buster that makes Mapbox (and the
 * browser) fetch the tiles again after a hazard has left the map.
 */
export function hazardTileUrl(origin: string, version = 0): string {
  const base = (origin || '').replace(/\/+$/, '');
  const url = `${base}/vector/${HAZARD_MARTIN_SOURCE}/{z}/{x}/{y}`;
  return version > 0 ? `${url}?t=${version}` : url;
}

export function hazardSourceSpec(origin: string, version = 0) {
  return {
    type: 'vector' as const,
    tiles: [hazardTileUrl(origin, version)],
    minzoom: HAZARD_MIN_ZOOM,
    maxzoom: HAZARD_MAX_TILE_ZOOM,
  };
}

export function hazardLayerSpec() {
  return {
    id: HAZARD_LAYER_ID,
    type: 'symbol' as const,
    source: HAZARD_SOURCE_ID,
    'source-layer': HAZARD_SOURCE_LAYER,
    minzoom: HAZARD_MIN_ZOOM,
    layout: {
      // The sprite name (`tree_blocked`), resolved from the style's sprite like
      // the POI icons; hazardMissingImage.ts covers a style without it.
      'icon-image': ['get', HAZARD_PROP_ICON],
      'icon-size': HAZARD_ICON_SIZE,
      'icon-anchor': HAZARD_ICON_ANCHOR,
      'icon-allow-overlap': true,
      'icon-ignore-placement': true,
      'symbol-sort-key': ['get', HAZARD_PROP_SORT],
    },
  };
}

/**
 * The hazard id a clicked tile feature carries, as a string — Martin encodes
 * it as a string, some encoders as a number. Null when there isn't a usable one.
 */
export function hazardIdFromFeature(feature: any): string | null {
  const raw = feature?.properties?.[HAZARD_PROP_ID];
  if (raw === null || raw === undefined) return null;
  if (typeof raw === 'number') return Number.isFinite(raw) ? String(Math.trunc(raw)) : null;
  if (typeof raw !== 'string') return null;
  const id = raw.trim();
  return id ? id : null;
}

/**
 * Whether the top-most rendered feature at a click is a hazard marker — so the
 * POI layer underneath doesn't open its own sheet for the same click.
 */
export function isClickOnHazard(features: any[] | null | undefined): boolean {
  return !!features && features.length > 0 && features[0]?.layer?.id === HAZARD_LAYER_ID;
}

interface HazardHitMap {
  getLayer(id: string): unknown;
  queryRenderedFeatures(point: unknown, options: { layers: string[] }): unknown[];
}

/**
 * Whether a click landed on a hazard marker — the same point the marker's own
 * layer listener answers to. For a map-wide click handler (overlay lines, …)
 * to step aside, so one click doesn't open the hazard and a second card too.
 */
export function clickLandsOnHazard(map: HazardHitMap | null | undefined, point: unknown): boolean {
  if (!map || !map.getLayer(HAZARD_LAYER_ID)) return false;
  return map.queryRenderedFeatures(point, { layers: [HAZARD_LAYER_ID] }).length > 0;
}

/**
 * [handler], left out of a click on a hazard marker: one click opens one card,
 * and at a marker that is the hazard's own. For the map's other click handlers
 * (land access, hike highlights, …).
 */
export function skipOnHazard<E extends { point: unknown }>(
  getMap: () => HazardHitMap | null | undefined,
  handler: (event: E) => void,
): (event: E) => void {
  return (event: E) => {
    if (clickLandsOnHazard(getMap(), event.point)) return;
    handler(event);
  };
}
